import { Autocomplete, Chip } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { OLTheme, defaultOLTheme } from "contexts/Theme/ThemeProvider";
import { FieldArray, useFormikContext } from "formik";
import { useMessage } from "hooks/intl";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { InputSize, inputStylesMultiLine } from "../styles";
import { TextInputForMultiInput } from "./TextInputForMultiInput";
function getErrorForField(errors: any, keyName: string): any {
  const trimmedFieldName = keyName.substr(keyName.lastIndexOf(".") + 1);
  return errors[trimmedFieldName];
}

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    error: {
      border: `1px solid ${theme.red500}!important`,
    },
    input: {
      flex: 1,
      marginTop: theme.spacing(10),
      width: "100%",
      [theme.breakpoints.up("md")]: {
        marginTop: theme.spacing(0),
        width: "auto",
      },
      "[dir='rtl'] & .MuiChip-deleteIcon": {
        margin: "0 -6px 0 5px",
      },
    },
    nestedInput: {
      width: "100%",
      ...inputStylesMultiLine,
    },
    block: {
      display: "block",
    },
  }),
);
const CustomAutocomplete = withStyles(() => ({
  tag: {
    borderRadius: 4,
    backgroundColor: `${defaultOLTheme.gray100} !important`,
    "& svg": {
      height: 19,
    },
  },
  input: {
    "&::placeholder": {
      textOverflow: "ellipsis !important",
    },
  },
}))(Autocomplete);
export function MultiInput({
  "data-test-id": dataTestId,
  className,
  formField,
  helperText,
  label,
  placeholder,
  size,
  status,
  valueType,
}: {
  "data-test-id"?: string;
  className?: string;
  formField: string;
  helperText?: string;
  label?: string | React.ReactNode;
  placeholder?: string;
  size?: InputSize;
  status?: any;
  valueType?: "number" | "text";
}) {
  const classes = useStyles();
  const { setFieldValue } = useFormikContext();
  const { values } = useFormikContext<string[]>();
  const { message } = useMessage();

  const [errorToDisplay, setErrorToDisplay] = useState<any>(
    status ? getErrorForField(status, formField) : {},
  );

  useEffect(() => {
    if (status) {
      setErrorToDisplay(getErrorForField(status, formField));
    }
  }, [status, formField]);

  const prepareHelperText = useCallback(() => {
    if (typeof status === "object") {
      if (Array.isArray(errorToDisplay)) {
        return errorToDisplay;
      } else if (typeof errorToDisplay === "object") {
        return (
          <>
            {Object.values(errorToDisplay).map((item: any, index: number) => (
              <span key={index} className={`${classes.block}`}>
                {item}
              </span>
            ))}
          </>
        );
      } else {
        return message({ id: "multiInputHelper" });
      }
    } else if (helperText) {
      return helperText;
    } else {
      return null;
    }
  }, [helperText, status, errorToDisplay, classes.block, message]);

  return (
    <FieldArray
      name={`${formField}`}
      render={() => (
        <>
          <CustomAutocomplete
            className={`${classes.input} ${className}`}
            defaultValue={_.get(values, formField)}
            disableClearable
            options={[]}
            clearOnBlur
            freeSolo
            multiple
            onChange={(_, newval) => {
              setErrorToDisplay({});

              if (valueType === "number") {
                let values = [...(newval as string[])];
                const numberValues = values.map((val) => Number(val));
                return setFieldValue(formField, numberValues);
              }

              return setFieldValue(formField, newval);
            }}
            onOpen={() => {}}
            popupIcon={<PlusIcon />}
            renderInput={(params) => {
              return (
                <TextInputForMultiInput
                  {...params}
                  autoComplete="off"
                  className={classes.nestedInput}
                  data-test-id={`${dataTestId}-input`}
                  helperText={prepareHelperText()}
                  label={label}
                  name={formField}
                  placeholder={placeholder}
                  size={size}
                  type={valueType === "number" ? "number" : "text"}
                />
              );
            }}
            renderTags={(value: any[], getTagProps) => {
              if (!Array.isArray(value)) {
                return;
              }
              return value?.map((option: string, index: number) => (
                <Chip
                  classes={{
                    root:
                      !Array.isArray(errorToDisplay) &&
                      errorToDisplay?.[index] &&
                      classes.error,
                  }}
                  deleteIcon={<XCloseIcon />}
                  label={`${option}`}
                  {...getTagProps({ index })}
                />
              ));
            }}
          />
        </>
      )}
    />
  );
}
